import api from './api';
import { User } from '../types';

interface DependencyCheck {
  canDelete: boolean;
  reasons: string[];
}

export const usersService = {
  getAll: async (options?: { organizationId?: string; isActive?: boolean; includeDeleted?: boolean }): Promise<User[]> => {
    const params: Record<string, string | boolean> = {};
    if (options?.organizationId) params.organizationId = options.organizationId;
    if (options?.isActive !== undefined) params.isActive = options.isActive;
    if (options?.includeDeleted) params.includeDeleted = true;
    const response = await api.get<User[]>('/users', { params });
    return response.data;
  },

  getById: async (id: string): Promise<User> => {
    const response = await api.get<User>(`/users/${id}`);
    return response.data;
  },

  create: async (data: Partial<User> & { password: string }): Promise<User> => {
    const response = await api.post<User>('/users', data);
    return response.data;
  },

  update: async (id: string, data: Partial<User> & { password?: string }): Promise<User> => {
    const response = await api.patch<User>(`/users/${id}`, data);
    return response.data;
  },

  checkDependencies: async (id: string): Promise<DependencyCheck> => {
    const response = await api.get<DependencyCheck>(`/users/${id}/check-dependencies`);
    return response.data;
  },

  // Soft delete
  delete: async (id: string): Promise<void> => {
    await api.delete(`/users/${id}`);
  },

  // Restore from archive
  restore: async (id: string): Promise<User> => {
    const response = await api.post<User>(`/users/${id}/restore`);
    return response.data;
  },

  // Permanent delete
  permanentDelete: async (id: string): Promise<void> => {
    await api.delete(`/users/${id}/permanent`);
  },

  // ==================== Provider User Management ====================

  // Get all users for a provider
  getByProvider: async (providerId: string, includeDeleted?: boolean): Promise<User[]> => {
    const params: Record<string, boolean> = {};
    if (includeDeleted) params.includeDeleted = true;
    const response = await api.get<User[]>(`/users/by-provider/${providerId}`, { params });
    return response.data;
  },

  // Create a user for a provider (first user becomes PROVIDER_MANAGER)
  createProviderUser: async (
    providerId: string,
    data: { email: string; password: string; fullName: string; isActive?: boolean }
  ): Promise<User> => {
    const response = await api.post<User>(`/users/provider/${providerId}`, data);
    return response.data;
  },

  // Update a provider user
  updateProviderUser: async (
    providerId: string,
    userId: string,
    data: Partial<User> & { password?: string }
  ): Promise<User> => {
    const response = await api.patch<User>(`/users/provider/${providerId}/${userId}`, data);
    return response.data;
  },

  // Delete a provider user
  deleteProviderUser: async (providerId: string, userId: string): Promise<void> => {
    await api.delete(`/users/provider/${providerId}/${userId}`);
  },

};
